import React from "react";
import { cn } from "../lib/api";

export function Button({ children, className, variant = "primary", size = "md", ...props }) {
  const variants = {
    primary: "bg-brand text-white hover:bg-brand-600 shadow-sm",
    ghost: "bg-transparent text-ink hover:bg-black/5",
    outline: "border border-black/10 bg-white text-ink hover:bg-black/[0.03]",
    dark: "bg-ink text-white hover:bg-ink-soft",
    danger: "bg-red-500 text-white hover:bg-red-600",
  };
  const sizes = {
    sm: "h-9 px-3 text-sm rounded-xl",
    md: "h-12 px-4 text-[15px] rounded-2xl",
    lg: "h-14 px-6 text-base rounded-2xl",
    icon: "h-11 w-11 rounded-2xl",
  };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 font-semibold btn-press disabled:opacity-50 disabled:pointer-events-none",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function Card({ className, children, ...props }) {
  return (
    <div
      className={cn(
        "bg-white rounded-3xl border border-black/[0.06] shadow-card p-5",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function Input({ className, label, error, ...props }) {
  return (
    <label className="block">
      {label && (
        <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">
          {label}
        </span>
      )}
      <input
        className={cn(
          "w-full h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none",
          "focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors",
          "placeholder:text-ink-muted/70 text-ink",
          className
        )}
        {...props}
      />
      {error && <span className="text-xs text-red-500 mt-1 block">{error}</span>}
    </label>
  );
}

export function Textarea({ className, label, ...props }) {
  return (
    <label className="block">
      {label && (
        <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">
          {label}
        </span>
      )}
      <textarea
        className={cn(
          "w-full min-h-[96px] p-3 rounded-2xl bg-white border border-black/10 outline-none",
          "focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors text-ink",
          className
        )}
        {...props}
      />
    </label>
  );
}

export function Select({ className, label, children, ...props }) {
  return (
    <label className="block">
      {label && (
        <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">
          {label}
        </span>
      )}
      <select
        className={cn(
          "w-full h-12 px-3 rounded-2xl bg-white border border-black/10 outline-none",
          "focus:border-brand focus:ring-2 focus:ring-brand/20 text-ink",
          className
        )}
        {...props}
      >
        {children}
      </select>
    </label>
  );
}

export function Chip({ children, tone = "default", className }) {
  const tones = {
    default: "bg-black/5 text-ink",
    success: "bg-brand-50 text-brand-700",
    danger: "bg-red-50 text-red-600",
    warning: "bg-amber-50 text-amber-700",
    dark: "bg-ink text-white",
  };
  return <span className={cn("chip", tones[tone], className)}>{children}</span>;
}

export function Sheet({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="sheet-overlay">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-x-0 bottom-0 max-w-[480px] mx-auto bg-white rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">{title}</h3>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10"
            data-testid="sheet-close-btn"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Empty({ title, subtitle, icon: Icon }) {
  return (
    <div className="text-center py-12 px-6">
      {Icon && (
        <div className="h-14 w-14 rounded-2xl bg-black/5 mx-auto mb-4 flex items-center justify-center">
          <Icon size={26} strokeWidth={1.75} />
        </div>
      )}
      <h4 className="font-semibold text-ink">{title}</h4>
      {subtitle && <p className="text-sm text-ink-muted mt-1">{subtitle}</p>}
    </div>
  );
}

export function Skeleton({ className }) {
  return <div className={cn("animate-pulse bg-black/[0.06] rounded-2xl", className)} />;
}
